import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserX, CalendarX, Phone, AlertTriangle, ClipboardCheck, Send } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { PageHeader, StatsCard, StatsGrid, EmptyState, AppButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import DataTable from "../Common/DataTable";
import NotifyGuardiansModal from "./NotifyGuardiansModal";
import "./attendance.scss";

/**
 * DailyAbsenteesPage — for a chosen date, lists every student who was absent
 * (not present / not on leave) in the classes held that day, with contact +
 * guardian details for follow-up. Also flags batches that were expected to meet
 * but where attendance wasn't recorded.
 *
 * Route: /attendance/absentees
 */
const STATUS_LABEL = {
  absent: "Absent",
  dropped: "Dropped",
  pending: "Not marked",
};

const DailyAbsenteesPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const today = new Date().toISOString().slice(0, 10);

  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchAbsentees = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get(`/attendance/absentees?date=${date}`);
      setData(d);
    } catch {
      setToast({ show: true, message: "Failed to load absentees.", type: "danger" });
      setData(null);
    }
    setLoading(false);
    // selectedBranchId re-fetches on global branch switch (X-Branch-Id header).
  }, [date, selectedBranchId]);

  useEffect(() => { fetchAbsentees(); }, [fetchAbsentees]);

  const summary = data?.summary || {};
  const absentees = data?.absentees || [];
  const notMarked = data?.notMarked || [];

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        backButton
        onBack={() => navigate("/attendance")}
        icon={UserX}
        title="Daily Absentees"
        description="Students who missed class on a given day, across all batches"
        actions={
          absentees.length > 0 ? (
            <AppButton variant="primary" size="sm" onClick={() => setNotifyOpen(true)}>
              <Send size={15} /> Notify Guardians
            </AppButton>
          ) : null
        }
      />

      {/* Date filter */}
      <div className="attendance-filters mb-3">
        <div className="attendance-filter-item">
          <label>Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <Shimmer height={48} count={6} />
      ) : (
        <>
          {/* Summary */}
          <StatsGrid className="attendance-analytics-stats mb-3">
            <StatsCard title="Classes Held" value={summary.sessionsHeld || 0} icon={ClipboardCheck} iconColor="blue" />
            <StatsCard title="Present" value={summary.present || 0} icon={UserX} iconColor="emerald" />
            <StatsCard title="Absent" value={summary.absent || 0} icon={UserX} iconColor="amber" />
            <StatsCard title="Not Marked" value={summary.notMarkedBatches || 0} icon={CalendarX} iconColor="violet" />
          </StatsGrid>

          {/* Batches expected but not marked */}
          {notMarked.length > 0 && (
            <div className="attendance-notmarked-banner mb-3">
              <AlertTriangle size={16} />
              <span>
                Attendance not recorded for {notMarked.length} batch{notMarked.length !== 1 ? "es" : ""} expected today:{" "}
                {notMarked.map((b) => b.batchName).join(", ")}
              </span>
            </div>
          )}

          {/* Absentee list */}
          {absentees.length === 0 ? (
            <EmptyState
              icon={UserX}
              title="No absentees"
              description="Either everyone attended, or no attendance has been marked for this date yet."
            />
          ) : (
            <DataTable
              data={absentees}
              columns={[
                { key: "name", label: "Student", render: (a) => a.name },
                { key: "batch", label: "Batch", render: (a) => a.batchName },
                { key: "branch", label: "Branch", render: (a) => a.branchName || "-" },
                {
                  key: "status",
                  label: "Status",
                  render: (a) => (
                    <span className="attendance-badge attendance-badge--pending">
                      {STATUS_LABEL[a.status] || "Absent"}
                    </span>
                  ),
                },
                {
                  key: "contact",
                  label: "Contact",
                  render: (a) =>
                    a.mobile ? (
                      <a className="attendance-contact" href={`tel:${a.mobile}`}>
                        <Phone size={13} /> {a.mobile}
                      </a>
                    ) : "-",
                },
                {
                  key: "guardian",
                  label: "Guardian",
                  render: (a) =>
                    a.guardianMobile ? (
                      <a className="attendance-contact" href={`tel:${a.guardianMobile}`}>
                        <Phone size={13} /> {a.guardianName || a.guardianMobile}
                      </a>
                    ) : "-",
                },
              ]}
            />
          )}
        </>
      )}

      <NotifyGuardiansModal
        show={notifyOpen}
        onHide={() => setNotifyOpen(false)}
        endpoint="/attendance/notify-absentees"
        payload={{ date }}
        count={absentees.length}
        title="Notify Absentee Guardians"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default DailyAbsenteesPage;
